import { useState, useCallback } from 'react';
import { WorkflowDto } from 'src/api/FiretrolApiClient';
import {
  getErrorMessage,
  showSuccessToast,
  toFormikFieldErrors,
} from 'src/utils';

/**
 * Base interface for workflow-enabled resources.
 * All entities that participate in workflows must have these properties.
 */
export interface IWorkflowResource {
  id?: string;
  workflow?: WorkflowDto | null;
  status?: string | null;
  stepIndex?: number | null;
}

/**
 * The module's save mutation (`useSaveChange`, `useSaveRfi`, …). Its own
 * `onSuccess` seeds the detail cache and its `onSettled` invalidates every
 * screen that shows the record, so this hook only orchestrates.
 */
export interface IWorkflowSaveMutation<TRequest> {
  mutateAsync: (variables: {
    jobId: number;
    request: TRequest;
  }) => Promise<unknown>;
  isPending: boolean;
}

/** The module's `useGenerate<Module>Pdf` mutation. */
export interface IWorkflowPdfMutation {
  mutateAsync: (variables: { jobId: number; id: string }) => Promise<unknown>;
  isPending: boolean;
}

export interface IWorkflowSubmissionMutations<TRequest> {
  save: IWorkflowSaveMutation<TRequest>;
  generatePdf?: IWorkflowPdfMutation;
}

/**
 * Configuration options for the useWorkflowSubmission hook.
 */
export interface UseWorkflowSubmissionOptions<
  TDto extends IWorkflowResource,
  TRequest,
> {
  /** The module's save mutation plus, where the workflow renders one, its PDF mutation. */
  mutations: IWorkflowSubmissionMutations<TRequest>;
  /** Converts form values to an API request DTO, applying status/stepIndex and any extra request overrides */
  toRequest: (
    values: TDto,
    overrides?: Partial<TRequest> & {
      status?: string;
      stepIndex?: number;
      saveOnly?: boolean;
    },
  ) => TRequest;
  /** Entity name for logging purposes (e.g., "RFI", "Submittal") */
  entityName: string;
  /** Optional toast configuration. If provided, toasts will be shown on success. */
  toast?: {
    /** Extracts the entity number/identifier for toast messages (e.g., "RFI-001") */
    getEntityNumber: (entity: TDto) => string;
  };
}

/**
 * Callback functions for workflow state changes.
 */
export interface UseWorkflowSubmissionCallbacks<TDto> {
  /** Called when a new entity is created (entity had no id before save) */
  onCreated?: (result: TDto) => void;
  /** Called when an existing entity is saved/updated */
  onSaved?: (result: TDto) => void;
  /** Updates the form values with the result from the API */
  setValues?: (values: TDto) => void;
}

/**
 * Return type for the useWorkflowSubmission hook.
 */
export interface UseWorkflowSubmissionResult<TDto, TRequest> {
  /** Submit the workflow to advance to a new status/step, optionally generating a PDF */
  submitWorkflow: (
    status: string,
    stepIndex: number,
    saveOnly?: boolean,
    extra?: Partial<TRequest>,
  ) => Promise<TDto | null>;
  /** Void the workflow (sets status to "Voided" and stepIndex to -1) */
  voidWorkflow: () => Promise<TDto | null>;
  /** Save without advancing the workflow */
  saveOnly: () => Promise<TDto | null>;
  /** Current error message, if any */
  error: string | null;
  /** Server field-level validation messages keyed by camelCase form field */
  fieldErrors: Record<string, string>;
  /** Whether a submission is currently in progress */
  isSubmitting: boolean;
}

/**
 * Orchestrates a workflow record submission: run the module's save mutation,
 * re-baseline the form from the response, then generate the PDF the next step
 * asks for. Cache refresh is the mutations' own responsibility.
 */
export function useWorkflowSubmission<TDto extends IWorkflowResource, TRequest>(
  values: TDto,
  jobId: number,
  options: UseWorkflowSubmissionOptions<TDto, TRequest>,
  callbacks?: UseWorkflowSubmissionCallbacks<TDto>,
): UseWorkflowSubmissionResult<TDto, TRequest> {
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const { mutations, toRequest } = options;
  const isSubmitting = mutations.save.isPending || generatingPdf;

  /**
   * Submit the workflow with a new status and step index.
   * Optionally generates a PDF if the next step requires it.
   */
  const submitWorkflow = useCallback(
    async (
      status: string,
      stepIndex: number,
      saveOnly = false,
      extra?: Partial<TRequest>,
    ): Promise<TDto | null> => {
      if (!jobId) return null;

      setError(null);
      setFieldErrors({});

      try {
        const isNew = !values.id || values.id === '';
        const nextStep = values.workflow?.steps?.[stepIndex];

        // Revising returns a record to Draft (status "Draft", stepIndex -1)
        // and must persist as saveOnly. The shared Reject path (stepIndex -1)
        // stays unforced so the backend still emits its WorkflowRejectedEvent
        // email; only PDF generation is suppressed for every stepIndex < 0.
        const isRevise = status === 'Draft' && stepIndex === -1;
        const effectiveSaveOnly = saveOnly || isRevise;

        const request = toRequest(values, {
          status,
          stepIndex,
          saveOnly: effectiveSaveOnly,
          ...extra,
        });
        const result = (await mutations.save.mutateAsync({
          jobId,
          request,
        })) as TDto;

        // Update form values if callback provided
        if (callbacks?.setValues) {
          callbacks.setValues(result);
        }

        // Call appropriate callback based on whether this was a create or update
        if (isNew && callbacks?.onCreated) {
          callbacks.onCreated(result);
        } else if (callbacks?.onSaved) {
          callbacks.onSaved(result);
        }

        // Show toast notification if configured
        if (options.toast) {
          const entityNumber = options.toast.getEntityNumber(result);
          if (isNew) {
            showSuccessToast(`${entityNumber} Created!`);
          } else if (status === 'Voided') {
            showSuccessToast(`${entityNumber} Voided!`);
          } else if (values.status !== result.status) {
            showSuccessToast(`${entityNumber} ${result.status}`);
          } else {
            showSuccessToast(`${entityNumber} Updated!`);
          }
        }

        // Generate PDF if the next step requires it and we have a generatePdf method
        if (
          !effectiveSaveOnly &&
          stepIndex >= 0 &&
          nextStep?.createPdf &&
          mutations.generatePdf &&
          result.id
        ) {
          setGeneratingPdf(true);
          try {
            await mutations.generatePdf.mutateAsync({
              jobId,
              id: result.id,
            });
          } catch (pdfErr: unknown) {
            setError(
              `Entity saved successfully, but PDF generation failed: ${getErrorMessage(
                pdfErr,
              )}`,
            );
          } finally {
            setGeneratingPdf(false);
          }
        }

        return result;
      } catch (err: unknown) {
        setError(getErrorMessage(err));
        setFieldErrors(toFormikFieldErrors(err));
        return null;
      }
    },
    [jobId, values, mutations, toRequest, callbacks, options],
  );

  /**
   * Void the workflow by setting status to "Voided" and stepIndex to -1.
   */
  const voidWorkflow = useCallback(async (): Promise<TDto | null> => {
    return submitWorkflow('Voided', -1, false);
  }, [submitWorkflow]);

  /**
   * Save the workflow without advancing to the next step.
   * Preserves the current status and stepIndex.
   */
  const saveOnly = useCallback(async (): Promise<TDto | null> => {
    const currentStatus = values.status ?? 'Draft';
    const currentStepIndex = values.stepIndex ?? 0;
    return submitWorkflow(currentStatus, currentStepIndex, true);
  }, [values.status, values.stepIndex, submitWorkflow]);

  return {
    submitWorkflow,
    voidWorkflow,
    saveOnly,
    error,
    fieldErrors,
    isSubmitting,
  };
}
